import type { ObjectCategory } from '@gmn-portal/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { payloadClient } from '.';
import type { FindPayloadApiClientParams } from './types';

// Query keys for better type safety and reusability
export const objectCategoryKeys = {
  all: ['object-categories'] as const,
  lists: () => [...objectCategoryKeys.all, 'list'] as const,
  list: (params: ObjectCategoryQueryParams) => [...objectCategoryKeys.lists(), params] as const,
  details: () => [...objectCategoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...objectCategoryKeys.details(), id] as const,
} as const;

export type ObjectCategoryQueryParams = FindPayloadApiClientParams<
  'object-categories',
  ObjectCategory
> & {
  disabled?: boolean;
};

export const useObjectCategories = (params?: ObjectCategoryQueryParams) => {
  return useQuery({
    queryKey: objectCategoryKeys.list(params ?? {}),
    enabled: params?.disabled !== true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'object-categories',
        ...params,
      });
      return response;
    },
  });
};

export const useObjectCategory = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: objectCategoryKeys.detail(id),
    enabled: !params?.disabled && !!id,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'object-categories',
        id,
      });
      return response;
    },
  });
};

export const useCreateObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ObjectCategory>) => {
      const response = await payloadClient.create({
        collection: 'object-categories',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
    },
  });
};

export const useUpdateObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ObjectCategory> }) => {
      const response = await payloadClient.updateById({
        collection: 'object-categories',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.detail(id) });
    },
  });
};

export const useDeleteObjectCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'object-categories',
        id,
      });
      return response;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: objectCategoryKeys.detail(id) });
    },
  });
};
